import { Component, EventEmitter, HostBinding, Input, Output, ChangeDetectionStrategy } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import {
  CompanyInfoPatch,
  CompanyRegisterLookupError,
  CompanyRegisterLookupErrorCode,
  CompanyRegisterProvider,
  CompanyRegisterService,
  NotificationService,
} from '../../services'

@Component({
  selector: 'company-register-lookup',
  templateUrl: './company-register-lookup.component.html',
  styleUrls: ['./company-register-lookup.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CompanyRegisterLookupComponent {
  @HostBinding('class.input-group-append') public readonly isInputGroupAppend = true
  @HostBinding('style.display') public get display(): 'flex' | 'none' {
    return this.isAvailable ? 'flex' : 'none'
  }

  @Input() public ico: string | number | null
  @Input() public country: string | null | undefined
  @Input() public currentValues: Partial<CompanyInfoPatch> = {}
  @Output() public companyInfoLoaded = new EventEmitter<CompanyInfoPatch>()
  @Output() public loadingChange = new EventEmitter<boolean>()

  public isLoading = false
  public get provider(): CompanyRegisterProvider | null {
    return this.companyRegisterService.getProvider(this.country)
  }
  public get isAvailable(): boolean {
    return !!this.provider
  }
  public get providerLabel(): string {
    return this.provider ? this.translateService.instant(`company-register.provider.${this.provider}`) : ''
  }

  constructor(
    private companyRegisterService: CompanyRegisterService,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {}

  public async lookup(): Promise<void> {
    if (this.isLoading || !this.isAvailable) {
      return
    }

    const requestedIco = this.normalize(this.ico)
    const requestedCountry = this.normalizeCountry(this.country)
    const requestedProvider = this.provider
    this.setLoading(true)
    try {
      const result = await this.companyRegisterService.lookupAddress(requestedCountry, requestedIco)
      if (!this.isCurrentRequest(requestedProvider, requestedCountry, requestedIco)) {
        return
      }
      if (
        this.hasConflictingValues(result.values) &&
        !confirm(
          this.translateService.instant('company-register.confirm-overwrite', {
            register: this.providerLabel,
          })
        )
      ) {
        return
      }
      this.companyInfoLoaded.emit(result.values)
      if (result.status === 'terminated') {
        this.notificationService.warning(this.translateService.instant('company-register.warning.terminated'))
      }
    } catch (error) {
      if (!this.isCurrentRequest(requestedProvider, requestedCountry, requestedIco)) {
        return
      }
      const errorCode = error instanceof CompanyRegisterLookupError ? error.code : 'unavailable'
      this.notificationService.error(
        this.translateService.instant(this.getErrorTranslationKey(errorCode), {
          register: this.providerLabel,
        })
      )
    } finally {
      this.setLoading(false)
    }
  }

  private setLoading(isLoading: boolean): void {
    this.isLoading = isLoading
    this.loadingChange.emit(isLoading)
  }

  private isCurrentRequest(provider: CompanyRegisterProvider | null, country: string, ico: string): boolean {
    return (
      this.provider === provider && this.normalizeCountry(this.country) === country && this.normalize(this.ico) === ico
    )
  }

  private hasConflictingValues(patch: CompanyInfoPatch): boolean {
    return (Object.keys(patch) as (keyof CompanyInfoPatch)[]).some(key => {
      const currentValue = this.normalize(this.currentValues?.[key])
      return !!currentValue && currentValue !== this.normalize(patch[key])
    })
  }

  private getErrorTranslationKey(errorCode: CompanyRegisterLookupErrorCode): string {
    switch (errorCode) {
      case 'invalid-ico':
        return 'company-register.error.invalid-ico'
      case 'not-found':
        return 'company-register.error.not-found'
      case 'ambiguous':
        return 'company-register.error.ambiguous'
      default:
        return 'company-register.error.unavailable'
    }
  }

  private normalizeCountry(value: unknown): string {
    return this.normalize(value).toUpperCase()
  }

  private normalize(value: unknown): string {
    return value === null || value === undefined ? '' : String(value).trim()
  }
}
